import { useEffect, useMemo, useState } from 'react';
import './ClientCards.css';

const fmt = (value) => Number(value || 0).toLocaleString();
const plural = (count, one, many = `${one}s`) => `${fmt(count)} ${count === 1 ? one : many}`;

function shortDate(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function initials(org) {
  if (org.brand_code) return org.brand_code.toUpperCase();
  const letters = String(org.name || '').replace(/[^A-Za-z]/g, '').toUpperCase();
  return letters.slice(0, 2) || '—';
}

/** One summary per organization: stores, schedules, client logins. */
function buildRows(organizations, outlets, schedules, clientUsers) {
  const byOrg = new Map(
    organizations.map((org) => [
      String(org.id),
      { org, stores: [], live: [], closed: [], upcoming: [], logins: [] },
    ]),
  );
  outlets.forEach((outlet) => byOrg.get(String(outlet.organization_id))?.stores.push(outlet));
  schedules.forEach((schedule) => {
    const row = byOrg.get(String(schedule.organization_id));
    if (!row) return;
    if (schedule.schedule_status === 'in_progress') row.live.push(schedule);
    else if (schedule.schedule_status === 'closed') row.closed.push(schedule);
    else row.upcoming.push(schedule);
  });
  clientUsers.forEach((user) => byOrg.get(String(user.client_organization_id))?.logins.push(user));

  return [...byOrg.values()].map((row) => {
    const lastClosed = row.closed.reduce(
      (latest, s) => (!latest || new Date(s.end_at) > new Date(latest) ? s.end_at : latest),
      null,
    );
    const nextUpcoming = row.upcoming.reduce(
      (soonest, s) => (!soonest || new Date(s.start_at) < new Date(soonest) ? s.start_at : soonest),
      null,
    );
    // Head-office logins first: they are the main contact.
    const logins = [...row.logins].sort(
      (a, b) =>
        (a.client_access_level === 'organization' ? 0 : 1) - (b.client_access_level === 'organization' ? 0 : 1) ||
        String(a.name || a.email).localeCompare(String(b.name || b.email)),
    );
    const cities = new Set(row.stores.map((s) => String(s.city || '').trim().toLowerCase()).filter(Boolean));
    return { ...row, logins, lastClosed, nextUpcoming, cities: cities.size };
  });
}

const SORTS = {
  name: { label: 'Name (A–Z)', compare: (a, b) => String(a.org.name).localeCompare(String(b.org.name)) },
  live: {
    label: 'Live first',
    compare: (a, b) => b.live.length - a.live.length || String(a.org.name).localeCompare(String(b.org.name)),
  },
  stores: { label: 'Most stores', compare: (a, b) => b.stores.length - a.stores.length },
  audits: { label: 'Most audits', compare: (a, b) => b.closed.length - a.closed.length },
  recent: {
    label: 'Latest audit',
    compare: (a, b) => new Date(b.lastClosed || 0) - new Date(a.lastClosed || 0),
  },
};

const PAGE_SIZES = [10, 25, 50, 100];

/** Page numbers to show: first, last, current ±1, with gaps as '…'. */
function pageList(current, total) {
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const list = [...pages].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out = [];
  list.forEach((n, index) => {
    if (index && n - list[index - 1] > 1) out.push(`gap-${n}`);
    out.push(n);
  });
  return out;
}

const Chevron = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

/**
 * Clients tab of "Clients & Stores": one row per client organization with its
 * stores, schedules and portal contact (like the list in the design).
 */
function ClientCards({ organizations, outlets, schedules, clientUsers, onEdit, onDelete, onViewStores, onManageLogins }) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('live');
  const [pageSize, setPageSize] = useState(() => {
    try {
      return Number(localStorage.getItem('clientCardsPageSize')) || 10;
    } catch {
      return 10;
    }
  });
  const [page, setPage] = useState(1);

  const rows = useMemo(
    () => buildRows(organizations, outlets, schedules, clientUsers),
    [organizations, outlets, schedules, clientUsers],
  );

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = term
      ? rows.filter(({ org, logins }) =>
          [org.name, org.brand_code, org.email, org.phone, org.address, ...logins.map((u) => `${u.name} ${u.email}`)]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
            .includes(term),
        )
      : rows;
    return [...list].sort(SORTS[sort].compare);
  }, [rows, search, sort]);

  const pages = Math.max(1, Math.ceil(shown.length / pageSize));
  const current = Math.min(page, pages);
  const pageRows = shown.slice((current - 1) * pageSize, current * pageSize);
  // New search / sort / page size: back to the first page.
  useEffect(() => setPage(1), [search, sort, pageSize]);

  const changePageSize = (value) => {
    setPageSize(value);
    try {
      localStorage.setItem('clientCardsPageSize', String(value));
    } catch {
      // storage unavailable: the choice lasts until the page is closed
    }
  };

  return (
    <div className="client-cards">
      <div className="client-cards__toolbar">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search client, brand code, contact…"
          aria-label="Search clients"
        />
        <label>
          Sort
          <select value={sort} onChange={(event) => setSort(event.target.value)}>
            {Object.entries(SORTS).map(([key, item]) => (
              <option key={key} value={key}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <span className="client-cards__count">{plural(shown.length, 'client')}</span>
      </div>

      {shown.length === 0 ? <p className="client-cards__empty">No client matches “{search}”.</p> : null}

      <ul className="client-cards__list">
        {pageRows.map(({ org, stores, live, closed, upcoming, logins, lastClosed, nextUpcoming, cities }) => {
          const contact = logins[0];
          return (
            <li key={org.id} className="client-row">
              <div className="client-row__identity">
                <span className="client-row__badge">{initials(org)}</span>
                <div className="client-row__name">
                  <div className="client-row__title">
                    <strong>{org.name}</strong>
                    {live.length ? (
                      <span className="client-row__live" title="Schedules in progress now">
                        <span className="client-row__dot" />
                        {fmt(live.length)} Live
                      </span>
                    ) : null}
                  </div>
                  <div className="client-row__codes">
                    <code>{org.brand_code || '—'}</code>
                    <span>
                      Schedule prefix: <b>{org.brand_code || '—'}</b>
                    </span>
                  </div>
                </div>
              </div>

              <div className="client-row__about">
                {org.address ? <span className="client-row__address">{org.address}</span> : null}
                {org.email ? <span>{org.email}</span> : null}
                {org.phone ? <span>{org.phone}</span> : null}
                {!org.address && !org.email && !org.phone ? <span className="client-row__muted">No company details</span> : null}
              </div>

              <button type="button" className="client-row__box client-row__box--stores" onClick={() => onViewStores(org)}>
                <strong>{plural(stores.length, 'Store')}</strong>
                <span>Registered{cities ? ` · ${plural(cities, 'city', 'cities')}` : ''}</span>
                <small>
                  View stores <Chevron />
                </small>
              </button>

              <div className="client-row__box client-row__box--audits">
                <strong>{plural(closed.length, 'Audit')}</strong>
                <span>Completed</span>
                <small>
                  {lastClosed ? `Last: ${shortDate(lastClosed)}` : 'None completed yet'}
                  {upcoming.length ? ` · ${fmt(upcoming.length)} upcoming` : ''}
                </small>
                {nextUpcoming ? <small className="client-row__next">Next: {shortDate(nextUpcoming)}</small> : null}
              </div>

              <div className="client-row__contact">
                {contact ? (
                  <>
                    <div className="client-row__contact-head">
                      <strong>{contact.name || contact.email}</strong>
                      <span className="client-row__portal client-row__portal--on">Portal active</span>
                    </div>
                    {contact.client_title ? <span>{contact.client_title}</span> : null}
                    <span className="client-row__mono">{contact.email}</span>
                    {contact.phone ? <span>{contact.phone}</span> : null}
                    {logins.length > 1 ? (
                      <button type="button" className="client-row__link" onClick={() => onManageLogins(org)}>
                        +{fmt(logins.length - 1)} more login{logins.length > 2 ? 's' : ''}
                      </button>
                    ) : null}
                  </>
                ) : (
                  <>
                    <div className="client-row__contact-head">
                      <strong className="client-row__muted">No portal login</strong>
                      <span className="client-row__portal">Portal off</span>
                    </div>
                    <button type="button" className="client-row__link" onClick={() => onManageLogins(org)}>
                      Add a client login
                    </button>
                  </>
                )}
              </div>

              <div className="client-row__actions">
                <button type="button" className="client-row__action" onClick={() => onEdit(org)}>
                  Edit
                </button>
                <button type="button" className="client-row__action client-row__action--danger" onClick={() => onDelete(org)}>
                  Delete
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {shown.length ? (
        <nav className="client-cards__pager" aria-label="Clients pages">
          <span className="client-cards__range">
            {fmt((current - 1) * pageSize + 1)}–{fmt(Math.min(current * pageSize, shown.length))} of {fmt(shown.length)}
          </span>
          <div className="client-cards__pages">
            <button type="button" onClick={() => setPage(current - 1)} disabled={current <= 1} aria-label="Previous page">
              ‹ Prev
            </button>
            {pageList(current, pages).map((item) =>
              typeof item === 'number' ? (
                <button
                  key={item}
                  type="button"
                  className={item === current ? 'client-cards__page--current' : ''}
                  aria-current={item === current ? 'page' : undefined}
                  onClick={() => setPage(item)}
                >
                  {item}
                </button>
              ) : (
                <span key={item} className="client-cards__gap">…</span>
              ),
            )}
            <button type="button" onClick={() => setPage(current + 1)} disabled={current >= pages} aria-label="Next page">
              Next ›
            </button>
          </div>
          <label>
            Per page
            <select value={pageSize} onChange={(event) => changePageSize(Number(event.target.value))}>
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        </nav>
      ) : null}
    </div>
  );
}

export default ClientCards;
